import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { resource } from "@util/resource";
import { DWS } from "@interfaces/dws";
import { SchedulingAdvancedFilters } from "../../../../interfaces/dws/winery-scheduling";
import * as moment from "moment";
import { ParticipantsDetails } from "@interfaces/dws/scheduling/sale/sale-reservation";
import { Reservation } from "@interfaces/reservation";
import { VwReservation } from "@interfaces/dws/reservation";

@Injectable({
  providedIn: "root",
})
export class SchedulingService {
  constructor() {}

  list(
    wineryId: string,
    filters?: WinerySchedulingFilters,
    page: number = 0,
    count: number = 999999,
    sortBy?: string
  ): Observable<DWS.Page<VwReservation>> {
    if (filters) {
      // Extract IDs from object arrays
      Object.keys(filters).forEach(key => {
        const value = (filters as any)[key];

        if (Array.isArray(value)) {
          const cleaned = value
            .map((v: any) => (v && typeof v === 'object' && 'id' in v ? v.id : v))
            .filter((v: any) => v != null && v !== '');

          if (cleaned.length === 0) {
            delete (filters as any)[key];
          } else {
            (filters as any)[key] = cleaned;
          }
        } else if (value === '' || value === null || value === undefined) {
          delete (filters as any)[key];
        }
      });
      // Remove all remaining empty string / null / undefined scalar fields
      Object.keys(filters).forEach(key => {
        const value = (filters as any)[key];
        if (value === '' || value === null || value === undefined) {
          delete (filters as any)[key];
        }
      });

      const startTime = filters?.dateRange?.start || filters?.startTime;
      const endTime = filters?.dateRange?.end || filters?.endTime;
      const creationStartTime = filters?.dateRangeCreation?.start || filters?.createdAt || "";
      const creationEndTime = filters?.dateRangeCreation?.end || filters?.createdUntil || "";
      const paid = filters?.paid;
      const invoiceRequest = filters?.invoiceRequest;
      const isSales = filters?.isSales;
      const checkedIn = filters?.checkedIn;

      if (startTime) {
        filters.startTime = moment(startTime)
          .startOf("day")
          .format("YYYY-MM-DD");
      } else {
        filters.startTime = startTime;
      }
      if (endTime) {
        filters.endTime = moment(endTime).endOf("day").format("YYYY-MM-DD");
      } else {
        filters.endTime = endTime;
      }

      if (creationStartTime) {
        filters.createdAt = moment(creationStartTime)
            .startOf("day")
            .format("YYYY-MM-DD");
      } else {
        delete filters.createdAt;
      }
      if (creationEndTime) {
        filters.createdUntil = moment(creationEndTime)
            .startOf("day")
            .format("YYYY-MM-DD");
      } else {
        delete filters.createdUntil;
      }
      delete filters.dateRangeCreation;

      // WS-2190: Payment date range filter
      const paymentDateStart = filters?.dateRangePaymentDate?.start;
      const paymentDateEnd = filters?.dateRangePaymentDate?.end;
      if (paymentDateStart) {
        (filters as any).paidAtFrom = moment(paymentDateStart).startOf("day").format("YYYY-MM-DD");
      }
      if (paymentDateEnd) {
        (filters as any).paidAtTo = moment(paymentDateEnd).endOf("day").format("YYYY-MM-DD");
      }
      delete filters.dateRangePaymentDate;

      [
        { key: "paid", value: paid },
        { key: "invoiceRequest", value: invoiceRequest },
        { key: "isSales", value: isSales },
        { key: "checkedIn", value: checkedIn },
      ].forEach(({ key, value }) => {
        if (Array.isArray(value)) {
          // If both yes and no are selected, remove the filter
          if (value.length >= 2) {
            (filters as any)[key] = [];
            return;
          }
          const first = value[0] as any;
          const v = typeof first === "object" && first !== null && "id" in first ? first.id : first;
          (filters as any)[key] = v === "yes" || v === true || v === "true" ? ["true"] : ["false"];
        } else if (typeof value === "string") {
          (filters as any)[key] = value === "yes" || value === "true" ? ["true"] : ["false"];
        } else if (typeof value === "boolean") {
          (filters as any)[key] = value ? ["true"] : ["false"];
        } else {
          delete (filters as any)[key];
        }
      });
      
      delete filters.dateRange;

      if (filters.total) {
        if (filters.total.min != null) filters.minTotal = filters.total.min * 100;
        else if ((filters.total as any).gte != null) filters.minTotal = (filters.total as any).gte * 100;
        if (filters.total.max != null) filters.maxTotal = filters.total.max * 100;
        else if ((filters.total as any).lte != null) filters.maxTotal = (filters.total as any).lte * 100;
        delete filters.total;
      }
    }
    const params: Record<string, any> = { page, count, wineryId, ...filters, sortBy };
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === undefined || params[key] === '') {
        delete params[key];
      }
    });
    return resource("crm://scheduling/page")
      .params(params)
      .get<DWS.Page<VwReservation>>();
  }

  getAdvancedFiltersElements(
    wineryId: string
  ): Observable<SchedulingAdvancedFilters> {
    return resource("crm://scheduling/advanced-filters")
      .params({ wineryId })
      .get<SchedulingAdvancedFilters>();
  }

  getPartecipantsDetails(
    reservationId: string
  ): Observable<ParticipantsDetails[]> {
    return resource("crm://scheduling/get-partecipants-details")
      .params({ reservationId })
      .get<ParticipantsDetails[]>();
  }
}

export interface WinerySchedulingFilters {
  reservationId?: string;
  experienceId?: string[];
  fullName?: string;
  adults?: number;
  reservationOrigin?: string[];
  origin?: string[];
  minTotal?: number;
  maxTotal?: number;
  roomId?: string[];
  employeeId?: string[];
  startTime?: string | Date;
  endTime?: string | Date;
  state?: Reservation.State[];
  lang?: string;
  type?: Reservation.Type[];
  createdAt?: string | Date;
  createdUntil?: string | Date;

  searchText?: string;

  dateRange?: { start: string; end: string };
  dateRangeCreation?: { start: string; end: string };
  /** WS-2190: Filter by payment date range (paidAt) */
  dateRangePaymentDate?: { start: string; end: string };
  paid?: string[];
  invoiceRequest?: string[];
  isSales?: string[];
  checkedIn?: string[];
  total?: { min: number; max: number };
}
